/**
 * Blender-style brush/circle select for object and edit mode.
 *
 * Press C to enter a persistent circle-select modal. Left drag selects;
 * Shift-left drag or middle drag subtracts; wheel changes the radius; Esc,
 * Enter, right mouse, or C exits. The first positive stroke replaces the
 * current selection, while later positive strokes add to it.
 */

import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { composeSceneObjectWorldMatrix } from "../app/collectionGraph.ts";
import { useStore } from "../app/store.ts";
import type {
  ObjectSelectionTarget,
  SelectionDeltaState,
} from "../app/storeTypes.ts";
import {
  captureSelectionDeltaState,
  pushSelectionUndo,
  undoManager,
} from "../app/storeUndo.ts";
import { entityIdToStoreKey } from "../ecs/EntityId.ts";
import { getRegistry } from "../ecs/queryStore.ts";
import { isCurveControlPoint } from "../modeling/curveOps.ts";
import type { BrushSelectionMode } from "../modeling/editSelection.ts";
import { vertexCentroid } from "../modeling/editSelection.ts";
import { topologyFaceCount, topologyFaceVerts } from "../modeling/topology.ts";
import { isWorldPointOccluded } from "./EditModeRenderer.tsx";

const MIN_RADIUS = 1;
const MAX_RADIUS = 500;
const RADIUS_WHEEL_FACTOR = 1.1;
const DAB_SPACING_FRACTION = 0.25;
const DAB_SPACING_MIN_PX = 2;

const _project = new THREE.Vector3();
const _world = new THREE.Vector3();
const _edgeLocal = new THREE.Vector3();
const _objMatrix = new THREE.Matrix4();

function createCircleOverlay(container: HTMLElement): HTMLDivElement {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.border = "1px solid rgba(255, 255, 255, 0.85)";
  el.style.background = "rgba(255, 255, 255, 0.05)";
  el.style.borderRadius = "50%";
  el.style.boxShadow = "0 0 0 1px rgba(0, 0, 0, 0.25)";
  el.style.pointerEvents = "none";
  el.style.zIndex = "11";
  el.style.display = "none";
  container.appendChild(el);
  return el;
}

function updateCircleOverlay(
  el: HTMLDivElement,
  containerRect: DOMRect,
  x: number,
  y: number,
  radius: number,
) {
  const diameter = radius * 2;
  el.style.left = `${x - radius - containerRect.left}px`;
  el.style.top = `${y - radius - containerRect.top}px`;
  el.style.width = `${diameter}px`;
  el.style.height = `${diameter}px`;
  el.style.display = "block";
}

function projectWorldToScreen(
  wx: number,
  wy: number,
  wz: number,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number; z: number } | null {
  _project.set(wx, wy, wz).project(camera);
  if (_project.z < -1 || _project.z > 1) return null;
  return {
    x: ((_project.x + 1) / 2) * rect.width + rect.left,
    y: ((-_project.y + 1) / 2) * rect.height + rect.top,
    z: _project.z,
  };
}

function projectLocalToScreen(
  lx: number,
  ly: number,
  lz: number,
  objMatrix: THREE.Matrix4,
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number; z: number } | null {
  _world.set(lx, ly, lz).applyMatrix4(objMatrix);
  return projectWorldToScreen(_world.x, _world.y, _world.z, camera, rect);
}

function pointInCircle(
  px: number,
  py: number,
  cx: number,
  cy: number,
  radius: number,
): boolean {
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= radius * radius;
}

function segmentCircleT(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  radius: number,
): number | null {
  const abx = bx - ax;
  const aby = by - ay;
  const lenSq = abx * abx + aby * aby;
  if (lenSq <= 1e-8) {
    return pointInCircle(ax, ay, cx, cy, radius) ? 0 : null;
  }
  const t = Math.max(
    0,
    Math.min(1, ((cx - ax) * abx + (cy - ay) * aby) / lenSq),
  );
  const px = ax + abx * t;
  const py = ay + aby * t;
  return pointInCircle(px, py, cx, cy, radius) ? t : null;
}

function setsEqual<T>(a: ReadonlySet<T> | null, b: ReadonlySet<T> | null) {
  if (a === b) return true;
  if (!a || !b) return a == null && b == null;
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

function targetKey(target: ObjectSelectionTarget): string {
  return `${target.kind}:${target.id}`;
}

function targetsEqual(
  a: readonly ObjectSelectionTarget[],
  b: readonly ObjectSelectionTarget[],
) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (targetKey(a[i]) !== targetKey(b[i])) return false;
  }
  return true;
}

function selectionDeltaStatesEqual(
  a: SelectionDeltaState,
  b: SelectionDeltaState,
): boolean {
  const ae = a.editSelection;
  const be = b.editSelection;
  const editEqual =
    ae === be ||
    (!!ae &&
      !!be &&
      ae.objectId === be.objectId &&
      ae.lastActiveVertex === be.lastActiveVertex &&
      ae.lastActiveEdge === be.lastActiveEdge &&
      ae.lastActiveFace === be.lastActiveFace &&
      setsEqual(ae.selectedVertices, be.selectedVertices) &&
      setsEqual(ae.selectedEdges, be.selectedEdges) &&
      setsEqual(ae.selectedFaces, be.selectedFaces));

  return (
    a.mode === b.mode &&
    a.cursor3DSelected === b.cursor3DSelected &&
    targetsEqual(a.selectedObjectTargets, b.selectedObjectTargets) &&
    setsEqual(a.lastObjectModeSelection, b.lastObjectModeSelection) &&
    editEqual &&
    ((!a.lastEditModeSelection && !b.lastEditModeSelection) ||
      (!!a.lastEditModeSelection &&
        !!b.lastEditModeSelection &&
        a.lastEditModeSelection.objectId === b.lastEditModeSelection.objectId &&
        a.lastEditModeSelection.granularity ===
          b.lastEditModeSelection.granularity &&
        setsEqual(
          a.lastEditModeSelection.selectedVertices,
          b.lastEditModeSelection.selectedVertices,
        ) &&
        setsEqual(
          a.lastEditModeSelection.selectedEdges,
          b.lastEditModeSelection.selectedEdges,
        ) &&
        setsEqual(
          a.lastEditModeSelection.selectedFaces,
          b.lastEditModeSelection.selectedFaces,
        )))
  );
}

function canArmBrushSelect(state: {
  editMode: unknown;
  workspaceMode: string;
  commandBuffer: unknown;
  boxSelectActive: boolean;
  brushSelectActive: boolean;
}): boolean {
  return (
    !state.commandBuffer &&
    !state.boxSelectActive &&
    !state.brushSelectActive &&
    (state.editMode != null || state.workspaceMode === "object")
  );
}

function applyBrushSelectAt(
  x: number,
  y: number,
  mode: BrushSelectionMode,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const state = useStore.getState();
  const radius = state.brushSelectRadius;
  if (state.editMode) {
    applyEditBrushSelect(x, y, radius, mode, camera, canvas);
  } else if (state.workspaceMode === "object") {
    applyObjectBrushSelect(x, y, radius, mode, camera, canvas);
  }
}

function applyEditBrushSelect(
  x: number,
  y: number,
  radius: number,
  mode: BrushSelectionMode,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const state = useStore.getState();
  const em = state.editMode;
  if (!em) return;
  const obj = state.sceneObjects.get(em.objectId);
  if (!obj) return;

  const rect = canvas.getBoundingClientRect();
  composeSceneObjectWorldMatrix(
    state.sceneObjects,
    state.collections,
    em.objectId,
    _objMatrix,
  ) ?? _objMatrix.identity();

  if (em.isCurve && em.curveEditVertices) {
    const selected = new Set<number>();
    const verts = em.curveEditVertices;
    const pointCount = verts.length / 3;
    for (let i = 0; i < pointCount; i++) {
      if (!isCurveControlPoint(i)) {
        const pointIndex = Math.floor(i / 3);
        const co = pointIndex * 3;
        const hl = co + 1;
        const hr = co + 2;
        if (
          !em.selectedVertices.has(co) &&
          !em.selectedVertices.has(hl) &&
          !em.selectedVertices.has(hr)
        ) {
          continue;
        }
      }
      const p = projectLocalToScreen(
        verts[i * 3],
        verts[i * 3 + 1],
        verts[i * 3 + 2],
        _objMatrix,
        camera,
        rect,
      );
      if (p && pointInCircle(p.x, p.y, x, y, radius)) selected.add(i);
    }
    if (selected.size > 0 || mode === "set") {
      state.brushSelectEdit(selected, new Set(), new Set(), mode);
    }
    return;
  }

  const { vertices, topology } = obj;
  const { granularity, hiddenVertices, hiddenFaces } = em;
  const xray = em.xray ?? false;
  const camOrigin = camera.position.clone();

  function visiblePointInCircle(
    lx: number,
    ly: number,
    lz: number,
    adjacentVerts?: number[],
  ): boolean {
    const p = projectLocalToScreen(lx, ly, lz, _objMatrix, camera, rect);
    if (!p || !pointInCircle(p.x, p.y, x, y, radius)) return false;
    if (xray) return true;
    const worldPos = _world.set(lx, ly, lz).applyMatrix4(_objMatrix);
    return !isWorldPointOccluded(worldPos, camOrigin, adjacentVerts);
  }

  const selVerts = new Set<number>();
  const selEdges = new Set<number>();
  const selFaces = new Set<number>();

  if (granularity === "vertex") {
    const vCount = vertices.length / 3;
    for (let i = 0; i < vCount; i++) {
      if (hiddenVertices.has(i)) continue;
      if (
        visiblePointInCircle(
          vertices[i * 3],
          vertices[i * 3 + 1],
          vertices[i * 3 + 2],
          [i],
        )
      ) {
        selVerts.add(i);
      }
    }
  } else if (granularity === "edge") {
    const edgeCount = topology.edges.length / 2;
    for (let i = 0; i < edgeCount; i++) {
      const a = topology.edges[i * 2];
      const b = topology.edges[i * 2 + 1];
      if (hiddenVertices.has(a) && hiddenVertices.has(b)) continue;

      const pa = projectLocalToScreen(
        vertices[a * 3],
        vertices[a * 3 + 1],
        vertices[a * 3 + 2],
        _objMatrix,
        camera,
        rect,
      );
      const pb = projectLocalToScreen(
        vertices[b * 3],
        vertices[b * 3 + 1],
        vertices[b * 3 + 2],
        _objMatrix,
        camera,
        rect,
      );
      if (!pa || !pb) continue;
      const t = segmentCircleT(pa.x, pa.y, pb.x, pb.y, x, y, radius);
      if (t == null) continue;
      if (!xray) {
        _edgeLocal.set(
          vertices[a * 3] + (vertices[b * 3] - vertices[a * 3]) * t,
          vertices[a * 3 + 1] + (vertices[b * 3 + 1] - vertices[a * 3 + 1]) * t,
          vertices[a * 3 + 2] + (vertices[b * 3 + 2] - vertices[a * 3 + 2]) * t,
        );
        const worldPos = _world.copy(_edgeLocal).applyMatrix4(_objMatrix);
        if (isWorldPointOccluded(worldPos, camOrigin, [a, b])) continue;
      }
      selEdges.add(i);
      selVerts.add(a);
      selVerts.add(b);
    }
  } else if (granularity === "face") {
    const fCount = topologyFaceCount(topology);
    for (let fi = 0; fi < fCount; fi++) {
      if (hiddenFaces.has(fi)) continue;
      const fv = topologyFaceVerts(topology, fi);
      const c = vertexCentroid(vertices, fv);
      if (!c) continue;
      if (visiblePointInCircle(c[0], c[1], c[2], fv)) {
        selFaces.add(fi);
      }
    }
  }

  if (
    selVerts.size > 0 ||
    selEdges.size > 0 ||
    selFaces.size > 0 ||
    mode === "set"
  ) {
    state.brushSelectEdit(selVerts, selEdges, selFaces, mode);
  }
}

function applyObjectBrushSelect(
  x: number,
  y: number,
  radius: number,
  mode: BrushSelectionMode,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  const rect = canvas.getBoundingClientRect();
  const registry = getRegistry();
  const hits = new Set<string>();

  for (const [, entity] of registry) {
    const transform = entity.components.transform;
    if (!transform) continue;
    const visibility = entity.components.visibility;
    if (visibility && !visibility.visible) continue;

    const projected = projectWorldToScreen(
      transform.position[0],
      transform.position[1],
      transform.position[2],
      camera,
      rect,
    );
    if (!projected || !pointInCircle(projected.x, projected.y, x, y, radius)) {
      continue;
    }
    hits.add(entityIdToStoreKey(entity.id));
  }

  if (hits.size > 0 || mode === "set") {
    useStore.getState().brushSelectObjects(hits, mode);
  }
}

function isEditableEventTarget(e: Event): boolean {
  const el = e.target as HTMLElement | null;
  if (!el?.tagName) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName === "INPUT") {
    const input = el as HTMLInputElement;
    return !input.readOnly && !input.disabled;
  }
  return false;
}

export function BrushSelectHandler() {
  const { camera, gl } = useThree();

  useEffect(() => {
    const canvas = gl.domElement;
    const parent = canvas.parentElement;
    if (!parent) return;
    const container: HTMLElement = parent;
    const overlay = createCircleOverlay(container);

    let active = false;
    let strokeActive = false;
    let pointerId = -1;
    let strokeMode: BrushSelectionMode = "add";
    let beforeSelection: SelectionDeltaState | null = null;
    let hasAppliedAny = false;
    let lastPointerX = 0;
    let lastPointerY = 0;
    let lastDabX = Number.NaN;
    let lastDabY = Number.NaN;
    let pendingDabs: { x: number; y: number; mode: BrushSelectionMode }[] = [];
    let rafId: number | null = null;

    function radius() {
      return useStore.getState().brushSelectRadius;
    }

    function setStoreFlag(v: boolean) {
      useStore.setState({ brushSelectActive: v });
    }

    function capturePointer(id: number) {
      try {
        canvas.setPointerCapture(id);
      } catch {
        // Ignore browsers that reject capture after an interrupted pointer stream.
      }
    }

    function releasePointer() {
      if (pointerId === -1) return;
      try {
        if (canvas.hasPointerCapture(pointerId)) {
          canvas.releasePointerCapture(pointerId);
        }
      } catch {
        // Capture may already be gone after blur/cancel.
      }
    }

    function updateOverlayAt(x = lastPointerX, y = lastPointerY) {
      if (!active) return;
      updateCircleOverlay(
        overlay,
        container.getBoundingClientRect(),
        x,
        y,
        radius(),
      );
    }

    function flushDab() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      const dabs = pendingDabs;
      pendingDabs = [];
      if (dabs.length === 0 || !active) return;
      for (const dab of dabs) {
        applyBrushSelectAt(dab.x, dab.y, dab.mode, camera, canvas);
      }
    }

    function scheduleDab(
      x: number,
      y: number,
      mode: BrushSelectionMode,
      force = false,
    ) {
      const spacing = Math.max(
        DAB_SPACING_MIN_PX,
        radius() * DAB_SPACING_FRACTION,
      );
      const dx = x - lastDabX;
      const dy = y - lastDabY;
      const dist = Math.hypot(dx, dy);

      function enqueue(dabX: number, dabY: number) {
        pendingDabs.push({ x: dabX, y: dabY, mode });
        lastDabX = dabX;
        lastDabY = dabY;
      }

      if (!Number.isFinite(dist)) {
        enqueue(x, y);
      } else if (dist >= spacing) {
        const startX = lastDabX;
        const startY = lastDabY;
        const steps = Math.floor(dist / spacing);
        for (let i = 1; i <= steps; i++) {
          const t = (i * spacing) / dist;
          enqueue(startX + dx * t, startY + dy * t);
        }
        if (force && (lastDabX !== x || lastDabY !== y)) {
          enqueue(x, y);
        }
      } else if (force) {
        enqueue(x, y);
      } else {
        return;
      }

      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        flushDab();
      });
    }

    function begin() {
      const s = useStore.getState();
      if (!canArmBrushSelect(s)) return;
      const rect = canvas.getBoundingClientRect();
      if (lastPointerX === 0 && lastPointerY === 0) {
        lastPointerX = rect.left + rect.width / 2;
        lastPointerY = rect.top + rect.height / 2;
      }
      active = true;
      strokeActive = false;
      pointerId = -1;
      beforeSelection = captureSelectionDeltaState(s);
      hasAppliedAny = false;
      lastDabX = Number.NaN;
      lastDabY = Number.NaN;
      setStoreFlag(true);
      canvas.style.cursor = "crosshair";
      updateOverlayAt();
    }

    function finish() {
      if (!active) return;
      flushDab();
      const before = beforeSelection;
      if (before) {
        const after = captureSelectionDeltaState(useStore.getState());
        if (!selectionDeltaStatesEqual(before, after)) {
          pushSelectionUndo(before, after);
          useStore.setState({
            canUndo: undoManager.canUndo,
            canRedo: undoManager.canRedo,
          });
        }
      }
      active = false;
      strokeActive = false;
      releasePointer();
      pointerId = -1;
      beforeSelection = null;
      pendingDabs = [];
      hasAppliedAny = false;
      overlay.style.display = "none";
      canvas.style.cursor = "";
      setStoreFlag(false);
    }

    function nextStrokeMode(subtract: boolean): BrushSelectionMode {
      const first = !hasAppliedAny;
      hasAppliedAny = true;
      if (subtract) return "subtract";
      return first ? "set" : "add";
    }

    function onKeyDown(e: KeyboardEvent) {
      if (isEditableEventTarget(e)) return;
      const key = e.key.toLowerCase();
      const plain = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;

      if (!active) {
        if (plain && key === "c") {
          begin();
          if (active) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }
        return;
      }

      if (
        key === "escape" ||
        key === "enter" ||
        key === "c" ||
        e.code === "NumpadEnter"
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
        finish();
        return;
      }

      if (key === "+" || key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        e.stopImmediatePropagation();
        const next = Math.min(MAX_RADIUS, radius() * RADIUS_WHEEL_FACTOR);
        useStore.getState().setBrushSelectRadius(next);
        updateOverlayAt();
        return;
      }
      if (key === "-" || e.code === "NumpadSubtract") {
        e.preventDefault();
        e.stopImmediatePropagation();
        const next = Math.max(MIN_RADIUS, radius() / RADIUS_WHEEL_FACTOR);
        useStore.getState().setBrushSelectRadius(next);
        updateOverlayAt();
        return;
      }

      if (!["shift", "control", "alt", "meta"].includes(key)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }

    function onPointerMove(e: PointerEvent) {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      if (!active) return;
      updateOverlayAt(e.clientX, e.clientY);
      if (!strokeActive || e.pointerId !== pointerId) return;
      e.preventDefault();
      e.stopPropagation();
      scheduleDab(e.clientX, e.clientY, strokeMode);
    }

    function onPointerDown(e: PointerEvent) {
      lastPointerX = e.clientX;
      lastPointerY = e.clientY;
      if (!active) return;

      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        finish();
        return;
      }

      if (e.button !== 0 && e.button !== 1) return;
      e.preventDefault();
      e.stopPropagation();
      pointerId = e.pointerId;
      capturePointer(pointerId);
      strokeActive = true;
      strokeMode = nextStrokeMode(e.button === 1 || e.shiftKey);
      scheduleDab(e.clientX, e.clientY, strokeMode, true);
      flushDab();
      if (strokeMode === "set") strokeMode = "add";
    }

    function onPointerUp(e: PointerEvent) {
      if (!active || !strokeActive || e.pointerId !== pointerId) return;
      e.preventDefault();
      e.stopPropagation();
      scheduleDab(e.clientX, e.clientY, strokeMode, true);
      flushDab();
      strokeActive = false;
      releasePointer();
      pointerId = -1;
      lastDabX = Number.NaN;
      lastDabY = Number.NaN;
    }

    function onWheel(e: WheelEvent) {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      const factor =
        e.deltaY > 0 ? RADIUS_WHEEL_FACTOR : 1 / RADIUS_WHEEL_FACTOR;
      const next = Math.max(
        MIN_RADIUS,
        Math.min(MAX_RADIUS, radius() * factor),
      );
      useStore.getState().setBrushSelectRadius(next);
      updateOverlayAt();
    }

    function onContextMenu(e: MouseEvent) {
      if (!active) return;
      e.preventDefault();
    }

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointermove", onPointerMove, true);
    canvas.addEventListener("pointerdown", onPointerDown, true);
    canvas.addEventListener("pointerup", onPointerUp, true);
    canvas.addEventListener("pointercancel", onPointerUp, true);
    canvas.addEventListener("wheel", onWheel, {
      passive: false,
      capture: true,
    });
    canvas.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("blur", finish);

    return () => {
      finish();
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointermove", onPointerMove, true);
      canvas.removeEventListener("pointerdown", onPointerDown, true);
      canvas.removeEventListener("pointerup", onPointerUp, true);
      canvas.removeEventListener("pointercancel", onPointerUp, true);
      canvas.removeEventListener("wheel", onWheel, true);
      canvas.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("blur", finish);
      overlay.remove();
    };
  }, [camera, gl]);

  return null;
}
